import { useCallback, useEffect, useRef } from 'react'

import { NodeTypes } from '@cloudike/web_files'
import { assetsActions } from 'features/assets/model/slice/assetsSlice'
import { boardsActions } from 'features/boards/model/slice/boardsSlice'
import { filesPreviewActions } from 'features/files-preview/model/slice/filesPreviewSlice'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { WSActions } from 'shared/constants/common'
import { getFilesSdk } from 'shared/sdk/files'
import { initSharedWs } from 'shared/websocket/ws'

import {
  getFilesPublicLinkNodeHrefSelector,
  getFilesPublicLinkShareIdSelector,
  getFilesPublicLinksTokenSelector
} from '../selectors/publicLinkFilesSelectors'
import { allAssetsSelector } from '../../assets/model/selectors/assetsSelectors'

interface Fs2Change {
  id: string | number
  state?: 'in_process' | 'done' | string
  action?: 'changed' | 'created' | string
  node_id: string
  type: NodeTypes
  fields?: Record<string, any>
  parent_id?: string
  _embedded?: {
    previews?: any
    [key: string]: any
  }
  [key: string]: any
}

interface SharedMessage {
  output?: { changes?: Fs2Change[] }
  group?: string
}

const isAssetType = (type: NodeTypes) => type === NodeTypes.FILE || type === NodeTypes.LINK

const normalizeNode = (node: any) => {
  if (!node) return node
  if (!node.id && node.node_id) {
    return {
      ...node,
      id: node.node_id
    }
  }
  return node
}

export const usePublicBoardsAssetsWS = ({ currentBoardId } : { currentBoardId: string }) => {
  const dispatch = useAppDispatch()
  const sharedId = useAppSelector(getFilesPublicLinkShareIdSelector)
  const token = useAppSelector(getFilesPublicLinksTokenSelector)
  const nodeHref = useAppSelector(getFilesPublicLinkNodeHrefSelector)
  const assets = useAppSelector(allAssetsSelector)

  const assetsRef = useRef(assets)
  const pendingChangesRef = useRef<Map<string | number, SharedMessage['output']>>(new Map())
  const inFlightAdditionsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    assetsRef.current = assets
  }, [assets])

  const deleteAsset = useCallback((assetId: string) => {
    dispatch(assetsActions.deleteAsset(assetId))
    dispatch(filesPreviewActions.deleteItem(assetId))
  }, [dispatch])

  const deleteBoard = useCallback((boardId: string) => {
    dispatch(boardsActions.deleteItems([boardId]))
  }, [dispatch])

  const resolveAssetId = useCallback((nodeId: string) => {
    const currentAssets = assetsRef.current ?? []
    const directMatch = currentAssets.find((asset: any) => asset.id === nodeId || asset.node_id === nodeId)
    if (directMatch) {
      return directMatch.id
    }

    const linkedMatch = currentAssets.find((asset: any) => asset?.linked_node_id === nodeId)
    return linkedMatch?.id ?? nodeId
  }, [])

  const fetchSharedNodeById = useCallback(async (nodeId: string) => {
    if (!nodeHref || !sharedId) {
      return null
    }

    try {
      const params = {
        preview_jwt: token,
        preview: true
      }
      const { publicLinksService } = getFilesSdk()
      const response = await publicLinksService.getNotMyFsNodeByLink(nodeHref, nodeId, params, token ?? undefined)
      return normalizeNode(response?.data)
    } catch (error) {
      console.error('Failed to fetch shared node by id', error)
      return null
    }
  }, [nodeHref, sharedId, token])

  const updateItemByType = useCallback(async (change: Fs2Change) => {
    const { node_id: nodeId, fields = {}, type } = change
    if (!nodeId) return
    const hasFieldsToUpdate = fields && Object.keys(fields).length > 0

    if (isAssetType(type)) {
      // Assets may arrive either with full field diffs or with removal markers
      const assetId = resolveAssetId(nodeId)

      if (hasFieldsToUpdate) {
        // Removal flag from WS (typical for links that were deleted)
        if (fields.is_trashed === true) {
          deleteAsset(assetId)
          return
        }

        // WS sometimes sends `{ updated: <date> }` when file is removed
        const fieldKeys = Object.keys(fields)
        if (fieldKeys.length === 1 && fieldKeys[0] === 'updated') {
          deleteAsset(assetId)
          return
        }

        // Parent change means asset left the current folder
        const parentId = fields.parent_id
        const isCurrentParentId = (parentId) => {
          return parentId === currentBoardId
        }
        const isRootParentId = (parentId) => {
          return parentId === ''
        }
        if (parentId && isCurrentParentId(parentId) && !isRootParentId(parentId)) {
          deleteAsset(assetId)
          const payload = { id: assetId, ...fields }
          dispatch(assetsActions.updateAsset(payload))
          dispatch(filesPreviewActions.updateItem(payload))
          return
        } else {
          return await addNewItemByType(change)
        }
      }

      // No fields were supplied – fetch fresh data for this asset
      const node = await fetchSharedNodeById(nodeId)
      if (node) {
        const normalized = { ...node, id: resolveAssetId(node.id) }
        dispatch(assetsActions.updateAsset(normalized))
        dispatch(filesPreviewActions.updateItem(normalized))
      }
      return
    } else {
      // Boards follow the same pattern: removal markers or partial diffs
      if (hasFieldsToUpdate) {
        // `is_trashed` implies board was removed from share
        if (fields.is_trashed === true) {
          deleteBoard(nodeId)
          return
        }

        dispatch(boardsActions.updateItem({ id: nodeId, ...fields }))
        return
      }

      // No fields – fallback to API fetch to stay in sync
      const node = await fetchSharedNodeById(nodeId)
      if (node) {
        dispatch(boardsActions.updateItem(node))
      }
    }
  }, [dispatch, fetchSharedNodeById, resolveAssetId, deleteAsset, deleteBoard])

  const addNewItemByType = useCallback(async (change: Fs2Change) => {
    const { node_id, type } = change
    if (!node_id) return

    if (isAssetType(type)) {
      if (inFlightAdditionsRef.current.has(node_id)) {
        return
      }

      inFlightAdditionsRef.current.add(node_id)
      try {
        const node = await fetchSharedNodeById(node_id)
        if (node) {
          // Preserve previews data from WebSocket change if available
          const nodeWithPreview = {
            ...node,
            ...(change._embedded?.previews && {
              _embedded: {
                ...node._embedded,
                previews: change._embedded.previews
              }
            })
          }

          dispatch(assetsActions.addAsset(nodeWithPreview))
          dispatch(filesPreviewActions.addItem(nodeWithPreview))
        }
      } finally {
        inFlightAdditionsRef.current.delete(node_id)
      }

      return
    }

    if (type === NodeTypes.DIR) {
      const node = await fetchSharedNodeById(node_id)
      if (node) {
        dispatch(boardsActions.addItem(node))
      }
    }
  }, [dispatch, fetchSharedNodeById])

  const processCompletedChange = useCallback(async (change: Fs2Change) => {
    if (!change?.action) {
      return
    }

    if (change.action === WSActions.CHANGED) {
      await updateItemByType(change)
      return
    }

    if (change.action === WSActions.CREATED) {
      await addNewItemByType(change)
    }
  }, [addNewItemByType, updateItemByType])

  useEffect(() => {
    if (!sharedId) {
      return
    }

    pendingChangesRef.current.clear()

    const ws = initSharedWs(sharedId)

    const handleSharedBoardChanges = async (message: SharedMessage) => {
      const { output, group } = message
      if (group && group !== 'fs2') {
        return
      }
      if (!output?.changes?.length) {
        return
      }

      for (const change of output.changes) {
        if (change.state === 'in_process') {
          pendingChangesRef.current.set(change.id, output)
          continue
        }

        if (change.state === 'done') {
          const pending = pendingChangesRef.current.get(change.id)?.changes?.[0]
          await processCompletedChange(pending || change)
          pendingChangesRef.current.delete(change.id)
          continue
        }

        await processCompletedChange(change)
      }
    }

    ws.addEventListener('default', handleSharedBoardChanges)

    return () => {
      ws.removeEventListener('default', handleSharedBoardChanges)
    }
  }, [processCompletedChange, sharedId])
}
